// Sponsor HQ — Meta Business Suite-style advertiser dashboard with side nav, clip editor, audiences, insights
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import DashboardQuickActions from "../components/DashboardQuickActions";
import { Target, Plus, TrendingUp, Eye, MousePointerClick, Trophy, BarChart3, Layout, Scissors, Users, Activity } from "lucide-react";
import { toast, Toaster } from "sonner";
import BannerEditor from "./sponsor/BannerEditor";
import ClipEditorSection from "./sponsor/ClipEditorSection";
import InsightsSection from "./sponsor/InsightsSection";

const SECTIONS = [
  { id: "overview",   label: "Overview",     icon: BarChart3 },
  { id: "campaigns",  label: "Campaigns",    icon: Target },
  { id: "banner",     label: "Banner Editor",icon: Layout },
  { id: "clip",       label: "Clip Editor",  icon: Scissors },
  { id: "audiences",  label: "Audiences",    icon: Users },
  { id: "insights",   label: "Insights",     icon: Activity },
];

export default function SponsorHQ() {
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [open, setOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [section, setSection] = useState("overview");
  const [form, setForm] = useState({ name: "", objective: "reach", budget: 500 });

  const load = () => api.get("/sponsor/dashboard").then(({ data }) => setDash(data)).catch(() => setDash(null));
  useEffect(() => {
    if (user) load();
  }, [user]);

  if (!user) return <div className="p-20 text-center text-white/40">Sign in as a sponsor or owner.</div>;
  if (user.role !== "sponsor" && user.role !== "owner") {
    return (
      <div className="p-20 text-center space-y-3">
        <Target className="w-12 h-12 mx-auto text-nexus-magenta" />
        <h2 className="font-display text-2xl">Sponsor access required</h2>
        <p className="text-white/50">Use the sponsor demo account or upgrade your role in Owner HQ → Users.</p>
      </div>
    );
  }
  if (!dash) return <div className="text-white/40">Loading sponsor deck…</div>;

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    await api.post("/campaigns", form);
    setOpen(false);
    setForm({ name: "", objective: "reach", budget: 500 });
    toast.success("Campaign launched");
    load();
  };

  const s = dash.summary;

  return (
    <div className="dash-meta space-y-6 p-4 lg:p-6 -mx-4 lg:-mx-6 rounded-2xl" data-testid="sponsor-hq">
      <Toaster theme="dark" position="top-center" />

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow mb-2 text-nexus-magenta">// advertiser business suite</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">{user.display_name}</h1>
          <div className="text-[11px] tracking-[0.3em] uppercase text-gray-500 mt-2">snapshot · {new Date(dash.snapshot_at).toLocaleTimeString()}</div>
          <DashboardQuickActions className="mt-4" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`/u/${user.id}`} className="btn-ghost text-[10px] rounded-full" data-testid="sponsor-view-profile"><Eye className="w-3.5 h-3.5" /> View profile</a>
          <button onClick={() => setOpen(true)} className="btn-ghost text-[10px] rounded-full"><Plus className="w-3.5 h-3.5" />Quick campaign</button>
          <button onClick={() => setBannerOpen(true)} className="btn-nexus" data-testid="new-banner-btn"><Plus className="w-3.5 h-3.5" />Banner Editor + AI</button>
        </div>
      </div>
      {bannerOpen && <BannerEditor onClose={() => { setBannerOpen(false); load(); }} />}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* LEFT SIDE NAV — Meta Business Suite style */}
        <aside className="lg:w-56 lg:flex-shrink-0" data-testid="sponsor-side-nav">
          <div className="bg-nexus-surface border border-white/10 lg:sticky lg:top-20">
            {SECTIONS.map((sec) => {
              const active = section === sec.id;
              return (
                <button
                  key={sec.id}
                  onClick={() => sec.id === "banner" ? setBannerOpen(true) : setSection(sec.id)}
                  data-testid={`sponsor-nav-${sec.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-[11px] tracking-[0.2em] uppercase font-display border-l-2 transition-colors ${
                    active ? "border-nexus-cyan text-nexus-cyan bg-white/5" : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <sec.icon className="w-4 h-4" />
                  <span>{sec.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0 space-y-10">

      {/* HEADLINE METRICS */}
      {(section === "overview" || section === "insights") && (
      <div className="grid lg:grid-cols-4 gap-px bg-white/5" data-testid="metrics-grid">
        <div className="bg-nexus-panel border-l-2 border-nexus-magenta p-6">
          <div className="label-eyebrow text-nexus-magenta">// spent</div>
          <div className="font-display font-black text-4xl mt-3 text-nexus-magenta">${s.total_spent.toLocaleString()}</div>
          <div className="text-[11px] text-white/40 tracking-wider mt-2">of ${s.total_budget.toLocaleString()} budget</div>
        </div>
        <div className="bg-nexus-surface p-6">
          <Eye className="w-5 h-5 text-nexus-cyan mb-3" />
          <div className="label-eyebrow">impressions</div>
          <div className="font-display font-black text-3xl mt-2">{s.total_impressions.toLocaleString()}</div>
        </div>
        <div className="bg-nexus-surface p-6">
          <MousePointerClick className="w-5 h-5 text-nexus-amber mb-3" />
          <div className="label-eyebrow">clicks</div>
          <div className="font-display font-black text-3xl mt-2">{s.total_clicks.toLocaleString()}</div>
          <div className="text-[11px] text-white/40 mt-1 tracking-wider">CTR {s.avg_ctr}%</div>
        </div>
        <div className="bg-nexus-surface p-6">
          <TrendingUp className="w-5 h-5 text-nexus-mint mb-3" />
          <div className="label-eyebrow">conversions</div>
          <div className="font-display font-black text-3xl mt-2">{s.total_conversions.toLocaleString()}</div>
          <div className="text-[11px] text-white/40 mt-1 tracking-wider">CPA ${s.cpa.toFixed(2)} · CPM ${s.cpm.toFixed(2)}</div>
        </div>
      </div>
      )}

      {/* CAMPAIGNS TABLE */}
      {(section === "overview" || section === "campaigns") && (
      <section className="space-y-3" data-testid="campaigns-section">
        <h2 className="font-display text-sm tracking-[0.3em] uppercase">Active Campaigns</h2>
        <div className="space-y-px bg-white/5">
          {dash.campaigns.length === 0 && <div className="bg-nexus-surface p-6 text-white/40 text-sm">No campaigns yet — launch one to start reach.</div>}
          {dash.campaigns.map((c) => {
            const pctSpent = c.budget ? (c.spent / c.budget) * 100 : 0;
            return (
              <div key={c.id} data-testid={`camp-${c.id}`} className="bg-nexus-surface p-5 grid sm:grid-cols-6 gap-4 items-center">
                <div className="sm:col-span-2">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="text-[11px] text-white/40 tracking-wider mt-1">{c.objective}</div>
                </div>
                <div>
                  <div className="label-eyebrow mb-1">budget</div>
                  <div className="font-mono text-sm">${c.spent.toFixed(0)} / ${c.budget.toFixed(0)}</div>
                  <div className="h-1 bg-white/5 mt-2 overflow-hidden"><div className="h-full bg-nexus-cyan" style={{ width: `${Math.min(pctSpent, 100)}%` }} /></div>
                </div>
                <div><div className="label-eyebrow mb-1">impressions</div><div className="font-mono text-sm">{c.impressions.toLocaleString()}</div></div>
                <div><div className="label-eyebrow mb-1">clicks · ctr</div><div className="font-mono text-sm">{c.clicks.toLocaleString()} · {c.ctr}%</div></div>
                <div className="text-right"><div className="label-eyebrow mb-1">conv.</div><div className="font-display text-lg text-nexus-mint">{c.conversions}</div></div>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* SPONSORED TAKEOVERS */}
      {(section === "overview" || section === "campaigns") && dash.sponsored_takeovers.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><Trophy className="w-4 h-4 text-nexus-amber" />Sponsored Takeovers</h2>
          <div className="space-y-px bg-white/5">
            {dash.sponsored_takeovers.map((t) => (
              <div key={t.id} className="bg-nexus-surface p-4 flex items-center gap-4">
                <div className="w-3 h-12" style={{ background: t.brand_color }} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{t.brand_name}</div>
                  <div className="text-[11px] text-white/40">{t.duration_hours}h · {t.active ? "live" : "ended"}</div>
                </div>
                <div className="font-display text-lg text-nexus-amber">${t.sponsor_fee}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CLIP EDITOR — Reels/Stories ad creative builder */}
      {section === "clip" && <ClipEditorSection />}

      {/* INSIGHTS — deeper analytics */}
      {section === "insights" && <InsightsSection dash={dash} />}

      </div>{/* end main */}
      </div>{/* end flex */}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setOpen(false)}>
          <div className="glass-strong p-8 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="campaign-modal">
            <h3 className="font-display text-xl tracking-tight">New campaign</h3>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" className="w-full bg-transparent border border-white/10 px-3 py-2" data-testid="campaign-name" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="w-full bg-nexus-surface border border-white/10 px-3 py-2">
                <option value="reach">Reach</option><option value="clicks">Clicks</option><option value="conversion">Conversion</option>
              </select>
              <input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) })} placeholder="Budget $" className="w-full bg-transparent border border-white/10 px-3 py-2" />
            </div>
            <button onClick={create} className="btn-nexus w-full" data-testid="create-campaign">Launch campaign</button>
          </div>
        </div>
      )}
    </div>
  );
}
